import React, { useEffect, useRef, useState } from 'react';
import {
  Typography,
  List,
  ListItem,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  CircularProgress,
  Box,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Container,
  Divider,
  IconButton,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Download,
  NavigateBefore,
  NavigateNext,
  CheckCircle,
  Assessment as AssessmentIcon,
  Quiz as QuizIcon,
  Assignment,
  Close
} from '@mui/icons-material';
import { addAssignment, addUserAssessmentResults } from '../Queries';
import { useReactToPrint } from 'react-to-print';
import PreviewUserResult from './PreviewUserResult';

const UserAssessmentForm = ({ assessment, userExist, takeAssessmentOpen, handleTakeAssessmentClose }) => {
  const [interpretations, setInterpretations] = useState([]);
  const [finalInterpretation, setFinalInterpretation] = useState(assessment?.assessmentResults?.[0]?.results?.finalInterpretation || []);
  const [finalRecommendation, setFinalRecommendation] = useState(assessment?.assessmentResults?.[0]?.results?.finalRecommendation || []);
  const [recommendations, setRecommendations] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState(assessment?.assessmentResults?.[0]?.results?.assessment_data || []);
  const [userSubmitted, setUserSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [finalResult, setFinalResult] = useState([]);
  const [finalScore, setFinalScore] = useState(assessment?.assessmentResults?.[0]?.results?.finalScore || 0);
  const [previewForm, setPreviewForm] = useState(false);
  const [isFormAlreadySubmitted, setIsFormAlreadySubmitted] = useState(false);

  const [userData] = useState(JSON.parse(localStorage.getItem('userData')));
  const questions = assessment?.questions || [];
  const printRef = useRef();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // Calculate progress percentage
  const progressPercentage = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;
  const answeredQuestions = answers?.filter(answer => answer && answer.answer && answer.answer.trim() !== '').length;

  // Get max score from JSON field with backward compatibility
  const getMaxScore = () => {
    if (assessment?.results) {
      try {
        const jsonData = typeof assessment.results === 'string' ? JSON.parse(assessment.results) : assessment.results;
        return jsonData.max_score || 0;
      } catch (error) {
        console.log('Error parsing JSON for max_score:', error);
      }
    }
    // Fallback to old max_score field for backward compatibility
    return assessment?.max_score || 0;
  };

  const getDropDowns = async () => {
    try {
      let questionAnswers = '';
      if (assessment?.assessmentResults && assessment?.assessmentResults && assessment?.assessmentResults?.length > 0) {
        const jsonData = assessment.assessmentResults;
        jsonData.forEach(item => {
          if (item.user_id === userData?.email) {
            questionAnswers = item.assessment_data;
            setAnswers(item.assessment_data);
          }
        });
      }

      if (assessment?.recommendations) {
        // setRecommendations(assessment.recommendations);
      }

      if (assessment?.interpretations) {
        setInterpretations(assessment.interpretations);
      }
    } catch (error) {
      console.error('Error getting dropdown data:', error);
    }
  };

  const handleNext = () => setCurrentQuestionIndex(prev => prev + 1);
  const handlePrevious = () => setCurrentQuestionIndex(prev => prev - 1);

  const handleOptionChange = (event) => {
    const selectedOption = event.target.value;
    const score = selectedOption.split('(Score: ')[1]?.replace(/\)$/g, '');
    setAnswers(prev => {
      const updated = [...prev];
      updated[currentQuestionIndex] = {
        question: questions[currentQuestionIndex]?.question,
        answer: selectedOption,
        score: score
      };
      return updated;
    });
  };

  const handleTextFieldChange = (event) => {
    const textValue = event.target.value;
    const currentQuestion = questions[currentQuestionIndex];
    
    setAnswers(prev => {
      const updated = [...prev];
      updated[currentQuestionIndex] = {
        question: currentQuestion?.question,
        answer: textValue,
        score: 0 // Text fields have zero score - this is intentional for open-ended questions
      };
      return updated;
    });
  };

  const calculateTotalScore = (selectedOptions) => {
    let totalScore = 0;
    const selectedAnswers = answers.length > 0 ? answers : selectedOptions;
    selectedAnswers.forEach(item => {
      // Handle both string and number scores, including zero values
      const score = item.score !== undefined && item.score !== null ? Number(item.score) : 0;
      totalScore += score;
    });
    setFinalScore(totalScore);
    return totalScore;
  };

  const findInterpretation = (template, interpretationData, questionAnswers) => {
    let totalA = 0, totalB = 0, totalC = 0;
    const selectedAnswers = answers.length > 0 ? answers : questionAnswers;

    const scoreResult = { totalA, totalB, totalC };
    let totalScore = 0;
    setFinalScore(scoreResult);
    const data = interpretations.length > 0 ? interpretations : interpretationData;
    const finalInterpretations = [];
    data?.forEach(item => {
      // if (template === 'Client Interaction') {
      const score = calculateTotalScore(selectedAnswers);
      totalScore = score;
      setFinalScore(score);
      if (item.minScore <= score && item.maxScore >= score) {
        finalInterpretations.push(item);
      }
      // }
    });
    if (finalInterpretations.length > 0) {
      // setFinalInterpretation(finalInterpretations);
      const match = assessment.recommendations.find(item =>
        finalInterpretations[0]?.name &&
        (item.name.trim().toLowerCase().includes(finalInterpretations[0].name.trim().toLowerCase()) ||
          finalInterpretations[0].name.trim().toLowerCase().includes(item.name.trim().toLowerCase()))
      );
      // setFinalRecommendation(match ? [match] : []);

      setIsFormAlreadySubmitted(false);
      setUserSubmitted(true);
      setFinalResult(finalInterpretations);
      return [scoreResult, finalInterpretations[0], match, totalScore];
    }
    return [scoreResult, []];
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const result = findInterpretation(assessment.assignment_type, assessment.interpretations);
      const jsonStructure = {
        user_id: userData?.email,
        user_details: userData,
        assessment_data: answers,
        finalScore: result[3],
        finalInterpretation: result[1],
        finalRecommendation: result[2]
      };
      setFinalInterpretation(result[1]);
      setFinalRecommendation(result[2]);
      setFinalScore(result[3]);
      // const updatedArray = updateData(assessment.json, userData?.email, jsonStructure);
      const formData = {
        id: new Date().getTime().toString(),
        assessment_id: assessment.form_id?.toString(),
        json: JSON.stringify(jsonStructure),
        user_id: userData?.email,
        created_at: new Date().toLocaleDateString(),
      };
      // const response = await addAssignment(formData, true);
      const response = await addUserAssessmentResults(formData);
      if (response.data?.statusCode === 200) {
        setIsFormAlreadySubmitted(false);
        setUserSubmitted(true);
        // handleRefresh?.();
      }
    } catch (error) {
      console.error('Error submitting assessment:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateData = (dataArray, userId, updatedData) => {
    dataArray = dataArray ? JSON.parse(dataArray) : [];
    const index = dataArray.findIndex(item => item.user_id === userId);
    if (index !== -1) dataArray[index] = updatedData;
    else dataArray.push(updatedData);
    return dataArray;
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    onAfterPrint: () => setPreviewForm(false),
  });

  const printReport = () => {
    setPreviewForm(true);
    setTimeout(() => handlePrint(), 100);
  };

  useEffect(() => {
    if (userExist) setIsFormAlreadySubmitted(true);
    getDropDowns();
  }, [assessment]);

  if (!assessment) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <AssessmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No assessment data found
          </Typography>
        </Card>
      </Container>
    );
  }

  return (
    <Dialog
      open={takeAssessmentOpen}
      onClose={handleTakeAssessmentClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: { borderRadius: 3, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }
      }}
    >
      <DialogTitle sx={{
        color: "#0a0068",
        fontWeight: 700,
        borderLeft: "4px solid #0a0068",
        background: 'linear-gradient(135deg, #f8f9ff 0%, #e8ebff 100%)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pr: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Assignment sx={{ mr: 1, verticalAlign: 'middle' }} />
          {assessment ? assessment.assignment_name : "Assessment"}
        </Box>
        <IconButton
          onClick={handleTakeAssessmentClose}
          sx={{
            color: '#0a0068',
            '&:hover': {
              backgroundColor: 'rgba(10, 0, 104, 0.1)'
            }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ borderLeft: "4px solid #0a0068" }}>
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
          {/* {isFormAlreadySubmitted ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={48} sx={{ mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  Loading your assessment results...
                </Typography>
              </Box>
            </Box>
          ) : ( */}
          <>
            {userSubmitted || userExist ? (
              <Card sx={{ boxShadow: 3 }}>
                <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <CheckCircle sx={{ color: 'success.main', fontSize: 32, mr: 2 }} />
                    <Typography variant="h5" sx={{ color: 'success.main', fontWeight: 600 }}>
                      Assessment Complete!
                    </Typography>
                  </Box>

                  <Box>
                    <Card sx={{ mb: 3, backgroundColor: '#f8f9fa' }}>
                      <CardContent>
                        <Typography
                          variant="h6"
                          sx={{ color: '#0a0068', fontWeight: 600, mb: 2 }}
                        >
                          Your Final Score
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                          <Chip
                            label={`Score: ${finalScore}${getMaxScore() ? ` / ${getMaxScore()}` : ''}`}
                            color="primary"
                            size="large"
                          />
                          <Chip
                            label={finalInterpretation.name || 'No Interpretation Available'}
                            color="secondary"
                            variant="outlined"
                          />
                        </Box>
                      </CardContent>
                    </Card>

                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Your Results
                    </Typography>

                    <Card sx={{ mb: 3 }}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#0a0068' }}>
                          Interpretations
                        </Typography>
                        {/* {finalInterpretation.map((item, index) => ( */}
                        <div
                          // key={index}
                          style={{ fontSize: '14px', lineHeight: 1.6, marginBottom: '16px' }}
                          dangerouslySetInnerHTML={{ __html: finalInterpretation?.description }}
                        />
                        {/* ))} */}
                      </CardContent>
                    </Card>

                    <Card sx={{ mb: 3, backgroundColor: '#e8f5e8' }}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#0a0068' }}>
                          Recommendations
                        </Typography>
                        {/* {finalRecommendation.map((item, index) => ( */}
                        <div
                          // key={index}
                          style={{ fontSize: '14px', lineHeight: 1.6, marginBottom: '16px' }}
                          dangerouslySetInnerHTML={{ __html: finalRecommendation?.description }}
                        />
                        {/* ))} */}
                      </CardContent>
                    </Card>
                  </Box>

                  <Button
                    variant="contained"
                    size="large"
                    onClick={printReport}
                    startIcon={<Download />}
                    sx={{
                      backgroundColor: '#0a0068',
                      '&:hover': { backgroundColor: '#080056' },
                      borderRadius: 2,
                      px: 4,
                      py: 1.5,
                      fontSize: '16px'
                    }}
                  >
                    Download Report
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Box>
                {/* Header Card */}
                <Card sx={{ mb: 3, boxShadow: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <QuizIcon sx={{ color: '#0a0068', fontSize: 32, mr: 2 }} />
                      <Typography variant="h5" sx={{ color: '#0a0068', fontWeight: 600 }}>
                        Assessment Instructions
                      </Typography>
                    </Box>
                    <div
                      style={{ fontSize: '14px', lineHeight: 1.6 }}
                      dangerouslySetInnerHTML={{ __html: assessment.instructions }}
                    />
                  </CardContent>
                </Card>

                {/* Progress Card */}
                <Card sx={{ mb: 3, backgroundColor: '#f8f9fa' }}>
                  <CardContent sx={{ pb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Progress
                      </Typography>
                      <Chip
                        label={`${answeredQuestions}/${questions.length} completed`}
                        color="primary"
                        size="small"
                      />
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={progressPercentage}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: '#0a0068'
                        }
                      }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Question {currentQuestionIndex + 1} of {questions.length}
                    </Typography>
                  </CardContent>
                </Card>

                {/* Question Card */}
                {questions.length > 0 && (
                  <Card sx={{ mb: 3, boxShadow: 2 }}>
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: '#0a0068',
                            fontSize: { xs: '16px', md: '18px' }
                          }}
                        >
                          {`${currentQuestionIndex + 1}. ${questions[currentQuestionIndex]?.question}`}
                        </Typography>
                        {questions[currentQuestionIndex]?.optionType === 'Text Field' && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              mt: 1,
                              color: 'text.secondary',
                              fontStyle: 'italic'
                            }}
                          >
                            Open-ended question (No scoring applied)
                          </Typography>
                        )}
                      </Box>

                      {/* Render based on optionType */}
                      {questions[currentQuestionIndex]?.optionType === 'Text Field' ? (
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          value={answers[currentQuestionIndex]?.answer || ''}
                          onChange={handleTextFieldChange}
                          placeholder="Please enter your answer here..."
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#0a0068',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#0a0068',
                              }
                            }
                          }}
                        />
                      ) : (
                        <RadioGroup
                          value={answers[currentQuestionIndex]?.answer || ''}
                          onChange={handleOptionChange}
                        >
                          {questions[currentQuestionIndex]?.options?.map((option, optionIndex) => (
                            <Card
                              key={optionIndex}
                              sx={{
                                mb: 1,
                                backgroundColor: answers[currentQuestionIndex]?.answer === option ? '#e3f2fd' : 'transparent',
                                border: answers[currentQuestionIndex]?.answer === option ? '2px solid #0a0068' : '1px solid #e0e0e0',
                                '&:hover': { backgroundColor: '#f5f5f5' },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <ListItem sx={{ py: 1 }}>
                                <FormControlLabel
                                  value={option}
                                  control={<Radio sx={{ color: '#0a0068' }} />}
                                  label={option.split('(Score')[0]}
                                  sx={{
                                    width: '100%',
                                    '& .MuiFormControlLabel-label': {
                                      fontSize: { xs: '14px', md: '16px' },
                                      lineHeight: 1.5
                                    }
                                  }}
                                />
                              </ListItem>
                            </Card>
                          ))}
                        </RadioGroup>
                      )}
                    </CardContent>
                  </Card>
                )}
              </Box>
            )}
          </>
          {/* )} */}

          {previewForm && (
            <div id="cust-print" style={{ display: 'none', marginTop: 20 }}>
              <div
                ref={printRef}
                style={{
                  margin: '10px',
                  width: '1150px',
                }}
              >
                <PreviewUserResult
                  assessment={assessment}
                  recommendation={finalRecommendation}
                  interpretation={finalInterpretation}
                  finalScore={finalScore}
                  client={userData?.user_batch}
                  currentUser={userData}
                />
              </div>
            </div>
          )}
        </Container>
      </DialogContent>

      {/* Navigation in Dialog Actions - Only show during assessment */}
      {!isFormAlreadySubmitted && !userSubmitted && !userExist && questions.length > 0 && (
        <DialogActions sx={{
          borderLeft: "4px solid #0a0068",
          p: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Button
            variant="outlined"
            disabled={currentQuestionIndex === 0}
            onClick={handlePrevious}
            startIcon={<NavigateBefore />}
            sx={{
              borderColor: '#0a0068',
              color: currentQuestionIndex === 0 ? 'text.disabled' : '#0a0068',
              '&:hover': { borderColor: '#080056', backgroundColor: '#f5f5f5' },
              minWidth: '120px'
            }}
          >
            Previous
          </Button>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center' }}
          >
            {currentQuestionIndex + 1} of {questions.length}
          </Typography>

          {currentQuestionIndex < questions.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!answers[currentQuestionIndex]?.answer || answers[currentQuestionIndex]?.answer.trim() === ''}
              endIcon={<NavigateNext />}
              sx={{
                backgroundColor: (!answers[currentQuestionIndex]?.answer || answers[currentQuestionIndex]?.answer.trim() === '') ? 'action.disabled' : '#0a0068',
                '&:hover': { backgroundColor: '#080056' },
                minWidth: '120px'
              }}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!answers[currentQuestionIndex]?.answer || answers[currentQuestionIndex]?.answer.trim() === '' || loading}
              sx={{
                backgroundColor: '#0a0068',
                '&:hover': { backgroundColor: '#080056' },
                minWidth: '120px'
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  Submitting...
                </Box>
              ) : (
                'Submit'
              )}
            </Button>
          )}
        </DialogActions>
      )}

      {/* Simple Close button for completed states */}
      {(isFormAlreadySubmitted || userSubmitted) && (
        <DialogActions sx={{ borderLeft: "4px solid #0a0068", p: 3 }}>
          <Button
            onClick={handleTakeAssessmentClose}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default UserAssessmentForm;